import Vue from 'vue';
import moment from 'moment';
import { mapStudentSchedule } from '../utils/scheduling/scheduleUtils.js';


const updateTeacherScheduleOccurrence = async (teacherScheduleId, originalDate, newStartTime, newEndTime, newClassroomId) => {

    const startDate = moment(newStartTime).format('YYYY-MM-DD');
    const endDate = startDate;
    const startTime = moment(newStartTime).format('HH:mm');
    const endTime = moment(newEndTime).format('HH:mm');
    const data = {
        startDate,
        endDate,
        startTime,
        endTime,
        classroomId: newClassroomId,
        timezone: new Date().getTimezoneOffset()
    }
    return await Vue.axios
        .put("/teacher-schedule/update-occurrence/" + teacherScheduleId, data, { params: { original_date: originalDate } })
        .then((resp) => {
            return resp.data;
        })
        .catch((error) => {
            throw error
        });
}

const updateStudentScheduleOccurrence = async (studentScheduleId, originalDate, newStartTime, newEndTime, newTeacherId, newClassroomId, newProgramId) => {

    const startDate = moment(newStartTime).format('YYYY-MM-DD');
    const endDate = startDate;
    const startTime = moment(newStartTime).format('HH:mm');
    const endTime = moment(newEndTime).format('HH:mm');
    const data = {
        startDate,
        endDate,
        startTime,
        endTime,
        teacherId: newTeacherId,
        classroomId: newClassroomId,
        programId: newProgramId,
        timezone: new Date().getTimezoneOffset()
    }
    return await Vue.axios
        .put("/student-schedule/update-occurrence/" + studentScheduleId, data, { params: { original_date: originalDate } })
        .then((resp) => {
            return mapStudentSchedule(resp.data);
        })
        .catch((error) => {
            throw error
        });
}


/**
 * Get all student schedule between date range
 * @param {*} startDate 
 * @param {*} endDate 
 * @returns 
 */
const fetchStudentSchedules = async (centerId, startDate, endDate, teacherIds = null, studentIds = null) => {
    const params = {
        size: 100000,
        teacher_ids: teacherIds ? teacherIds.join(',') : null,
        student_ids: studentIds ? studentIds.join(',') : null,
        center_id: centerId,
        start_date: startDate,
        end_date: endDate,
    };

    return await Vue.axios
        .get("/student-schedule", { params })
        .then((resp) => {
            return resp.data;
        })
        .catch((error) => {
            throw error
        });
}

/**
 * Fetch all the instances for a recurring schedule between date range
 * @param {*} centerId 
 * @param {*} startDate 
 * @param {*} endDate 
 * @param {*} scheduleId 
 * @returns 
 */
const fetchStudentScheduleInstances = async (centerId, startDate, endDate, scheduleId) => {
    const params = {
        size: 100000,
        schedule_id: scheduleId,
        center_id: centerId,
        start_date: startDate,
        end_date: endDate,
    };

    return await Vue.axios
        .get("/student-schedule/get-instances", { params })
        .then((resp) => {
            const list = resp.data;
            if(list){
                return list.map(mapStudentSchedule);
            }
            else{
                return []
            }
        })
        .catch((error) => {
            throw error
        });
}


/**
 * Get all staff schedule between date range
 * @param {*} startDate 
 * @param {*} endDate 
 * @returns 
 */
const fetchStaffSchedules = async (centerId, startDate, endDate, teacherIds = null) => {
    const params = {
        size: 100000,
        teacher_ids: teacherIds ? teacherIds.join(',') : null,
        center_id: centerId,
        start_date: startDate,
        end_date: endDate,
    };

    return await Vue.axios
        .get("/teacher-schedule", { params })
        .then((resp) => {
            return resp.data;
        })
        .catch((error) => {
            throw error
        });
}


const fetchStaffAvailabilities = async (centerId, startDate, endDate, teacherIds = null) => {
    const params = {
        teacher_ids: teacherIds ? teacherIds.join(',') : null,
        start_date: startDate,
        end_date: endDate,
        center_id: centerId,
    };
    return await Vue.axios.get("/teachers/availability/get-availability-by-dates", { params })
        .then((resp) => {
            return resp.data;
        })
        .catch((error) => {
            throw error;
        });
}


const fetchClassrooms = async (centerId) => {
    const params = {
        size: 1000,
        centerId
    };

    return await Vue.axios.get("/classroom", { params })
        .then((resp) => {
            // Include a default "Unassigned" teacher
            const classrooms = [...resp.data.content];

            // Sort classroom by name
            classrooms.sort((a, b) => {
                // Ignore case
                const nameA = a.name ? a.name.toUpperCase() : '';
                const nameB = b.name ? b.name.toUpperCase() : '';
                if (nameA < nameB) {
                    return -1;
                }
                if (nameA > nameB) {
                    return 1;
                }
                return 0;
            });

            return classrooms;
        })
        .catch((error) => {
            throw error
        });
}


const fetchTeachers = async (centerId, teacherIds) => {
    const params = {
        centerId,
        lite: true,
        teacherIds: teacherIds.join(',')
    };

    return await Vue.axios.get("/teacher/get-by-ids", { params })
        .then((resp) => {
            // Include a default "Unassigned" teacher
            const teachers = [...resp.data];

            // Sort teachers by firstName
            teachers.sort((a, b) => {
                // Ignore case
                const nameA = a.firstName ? a.firstName.toUpperCase() : '';
                const nameB = b.firstName ? b.firstName.toUpperCase() : '';
                if (nameA < nameB) {
                    return -1;
                }
                if (nameA > nameB) {
                    return 1;
                }
                return 0;
            });

            return teachers;
        })
        .catch((error) => {
            throw error
        });
}



const fetchStudents = async (centerId, studentIds) => {
    const params = {
        centerId,
        ids: studentIds.join(',')
    };

    return await Vue.axios.get("/student/get-by-ids", { params })
        .then((resp) => {
            // Include a default "Unassigned" teacher
            return resp.data;
        })
        .catch((error) => {
            throw error
        });
}

const groupByEntity = ({ teacherSchedules, studentSchedules, teachers, students, classrooms = [], teacherAvailabilities }) => {
    const teacherGroups = teachers.map((teacher) => {
        const teacherId = teacher.id;
        const groupedStudents = studentSchedules
            .filter((schedule) => schedule.tempTeacherId ? schedule.tempTeacherId === teacherId : schedule.teacherId === teacherId)
            .map((schedule) => {
                const student = students.find(student => student.id === schedule.studentId);
                return {
                    ...schedule,
                    studentFirstName: student ? student.firstName : '',
                    studentLastName: student ? student.lastName : '',
                }
            });

        return {
            teacherId,
            teacher,
            schedules: teacherSchedules.filter((schedule) => schedule.teacherId === teacherId),
            students: groupedStudents,
            availability: teacherAvailabilities.filter((a) => a.teacherId === teacherId),
        };
    });

    const classroomGroups = classrooms.map((classroom) => {
        const classroomId = classroom.id;
        const groupedStudents = studentSchedules
            .filter((schedule) => schedule.tempClassroomId ? schedule.tempClassroomId === classroomId : schedule.classroomId === classroomId)
            .map((schedule) => {
                const student = students.find(student => student.id === schedule.studentId);
                return {
                    ...schedule,
                    studentFirstName: student ? student.firstName : '',
                    studentLastName: student ? student.lastName : '',
                }
            });

        const groupedTeacherSchedules = teacherSchedules
            .filter((schedule) => schedule.classroomId === classroomId)
            .map((schedule) => {
                const teacher = teachers.find(t => t.id == schedule.teacherId)
                const teacherFirstName = teacher.firstName;
                const teacherLastName = teacher.lastName;
                return {
                    ...schedule,
                    teacherFirstName: teacherFirstName,
                    teacherLastName: teacherLastName,
                }
            });

        return {
            classroomId,
            classroom,
            schedules: groupedTeacherSchedules,
            students: groupedStudents
        };
    });

    const unassignedStudents = studentSchedules
        .filter((schedule) => !schedule.tempTeacherId && !schedule.teacherId && !schedule.tempClassroomId && !schedule.classroomId)
        .map((schedule) => {
            const student = students.find(student => student.id === schedule.studentId);
            return {
                ...schedule,
                studentFirstName: student ? student.firstName : '',
                studentLastName: student ? student.lastName : '',
            }
        });

    const unassignedGroup = {
        unassigned: true,
        students: unassignedStudents,
    }

    return [...teacherGroups, ...classroomGroups, unassignedGroup];
};



/**
 * Get all staff timesheet and schedules between date range
 * @param {*} startDate 
 * @param {*} endDate 
 * @returns 
 */
const fetchStaffAndStudentSchedules = async (centerId, startDate, endDate, teacherIds = null) => {
    const start = moment(startDate).format('YYYY-MM-DD')
    const end = moment(endDate).format('YYYY-MM-DD')
    const teacherAvailabilitiesPromise = fetchStaffAvailabilities(centerId, start, end, teacherIds);
    const teacherSchedulesPromise = fetchStaffSchedules(centerId, start, end, teacherIds);
    const studentSchedulesPromise = fetchStudentSchedules(centerId, start, end, teacherIds);

    let [teacherAvailabilities, teacherSchedules, studentSchedules] = await Promise.all([teacherAvailabilitiesPromise, teacherSchedulesPromise, studentSchedulesPromise]);

    // Get unique teacherId from all lists and fetch teacher info from server:
    const scheduleTeacherIds = teacherSchedules ? teacherSchedules.map(s => s.teacherId) : [];
    const availabilityTeacherIds = teacherAvailabilities ? teacherAvailabilities.map(a => a.teacherId) : [];
    const studentScheduleTeacherIds = studentSchedules ? studentSchedules.map(s => s.teacherId) : [];

    // Get unique studentId from student schedules
    const studentIds = [...new Set(studentSchedules.map(s => s.studentId))];

    // Combine teacherIds from all lists and remove duplicates
    let uniqueTeacherIds = [...new Set([...scheduleTeacherIds, ...availabilityTeacherIds, ...studentScheduleTeacherIds])];

    // Add null to the teacherIds array to fetch the "Unassigned" teacher
    uniqueTeacherIds.push(null);

    const teachersPromise = fetchTeachers(centerId, uniqueTeacherIds);
    const classroomsPromise = fetchClassrooms(centerId);
    const studentsPromise = fetchStudents(centerId, studentIds);
    const [teachers, students, classrooms] = await Promise.all([teachersPromise, studentsPromise, classroomsPromise]);

    const groupedData = groupByEntity({ teacherSchedules, studentSchedules, teachers, students, classrooms, teacherAvailabilities });

    // return grouped data, teachers and students
    return {
        groupedData,
        teachers,
        students,
        classrooms
    };
}



const assignClassroomsBatch = async (schedules) => {

    return await Vue.axios.post('/student-schedule/assign-classroom', schedules)
        .then((resp) => {
            return resp.data;
        })
        .catch((error) => {
            throw error
        });
};

const assignTeachersBatch = async (schedules) => {

    return await Vue.axios.post('/student-schedule/assign-teacher', schedules)
        .then((resp) => {
            return resp.data;
        })
        .catch((error) => {
            throw error
        });
};

export {
    fetchStaffSchedules,
    fetchStudentSchedules,
    fetchStudentScheduleInstances,
    fetchStaffAndStudentSchedules,
    assignTeachersBatch,
    assignClassroomsBatch,
    updateStudentScheduleOccurrence,
    updateTeacherScheduleOccurrence,
}